
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { DocumentProps } from './useSubmittedDocuments';
import { useDocumentEmailNotifier } from './notifications/useDocumentEmailNotifier';
import { useDocumentNotifier } from './notifications/useDocumentNotifier';
import { useProfileVerificationStatus } from './useProfileVerificationStatus';

export const useDocumentVerification = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { notifyDocumentVerified, notifyDocumentRejected } = useDocumentEmailNotifier();
  const { createDocumentNotification } = useDocumentNotifier();
  const { updateProfileVerificationStatus, createVerificationHistoryRecord } = useProfileVerificationStatus();

  const verifyDocument = async (
    document: DocumentProps, 
    status: 'verified' | 'rejected',
    notes?: string,
    setPrimary: boolean = true
  ) => {
    try {
      setIsProcessing(true);
      
      console.log(`Starting document verification for document ${document.id} with status: ${status}`);
      
      // Check if document is expired
      if (document.expiry_date && new Date(document.expiry_date) < new Date() && status === 'verified') {
        if (!confirm("This document appears to be expired. Are you sure you want to verify it?")) {
          setIsProcessing(false);
          return;
        }
        
        // Add note about expired document
        notes = (notes || '') + ' [Warning: Document approved despite expiration]';
      }
      
      // Get current user (admin/verifier) ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Authentication required to verify documents');
      
      const verifierId = user.id;
      
      // First check if this user already has verified documents
      const { data: existingDocs, error: checkError } = await supabase
        .from('user_id_documents')
        .select('id')
        .eq('user_id', document.user_id)
        .eq('verification_status', 'verified');
      
      if (checkError) throw checkError;
      
      // Determine if this should be set as primary based on whether other verified documents exist
      const shouldSetPrimary = setPrimary && 
        (status === 'verified') && 
        (!existingDocs || existingDocs.length === 0);
      
      // Update document status
      const { error } = await supabase
        .from('user_id_documents')
        .update({
          verification_status: status,
          notes: notes || null,
          verified_by: verifierId,
          primary_document: shouldSetPrimary,
          updated_at: new Date().toISOString()
        })
        .eq('id', document.id);

      if (error) throw error;

      console.log(`Document ${document.id} status updated to ${status}`);

      // Update profile verification status - always proceed for verified documents
      if (status === 'verified') {
        try {
          await updateProfileVerificationStatus(document.user_id, status, notes);
          console.log(`Profile verification status updated successfully for user ${document.user_id}`);
        } catch (profileError) {
          console.error('Profile update error:', profileError);
          // Don't fail the entire process if profile update fails
          toast.error('Document verified but profile update failed. Please check admin logs.');
        }
      } else if (status === 'rejected' && shouldSetPrimary) {
        // If rejecting a document and no other verified documents exist, update profile status
        try {
          await updateProfileVerificationStatus(document.user_id, status, notes);
        } catch (profileError) {
          console.error('Profile update error:', profileError);
        }
      }
      
      // Create verification history record for audit
      await createVerificationHistoryRecord(
        document.user_id,
        document.id,
        status,
        verifierId,
        notes
      );

      console.log(`Sending notifications for ${status} document`);

      // Send email notification with improved error handling
      try {
        if (status === 'verified') {
          await notifyDocumentVerified(document.user_id);
          console.log('Document verified email notification sent successfully');
        } else {
          await notifyDocumentRejected(document.user_id, undefined, notes);
          console.log('Document rejected email notification sent successfully');
        }
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
        // Don't fail the verification process if email fails
        toast.error('Document processed successfully but email notification failed');
      }

      // Create in-app notification with improved error handling
      try {
        await createDocumentNotification(document.user_id, status, notes);
        console.log('In-app notification created successfully');
      } catch (notificationError) {
        console.error('In-app notification failed:', notificationError);
        // Don't fail the verification process if in-app notification fails
      }

      toast.success(`Document ${status === 'verified' ? 'approved' : 'rejected'} successfully`);
      
      // Log for audit purposes
      console.log(`Document ${document.id} ${status} by ${verifierId} at ${new Date().toISOString()} with notes: ${notes || 'none'}`);
      
    } catch (error: any) {
      console.error('Error updating document verification:', error);
      toast.error(error.message || 'Failed to update document verification');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Method to update the primary document status
  const updatePrimaryDocumentStatus = async (documentId: string, userId: string) => {
    try {
      setIsProcessing(true);
      
      // First reset primary status on all documents for this user
      const { error: resetError } = await supabase
        .from('user_id_documents')
        .update({
          primary_document: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('verification_status', 'verified');
      
      if (resetError) throw resetError;
      
      // Set the selected document as primary
      const { error } = await supabase
        .from('user_id_documents')
        .update({
          primary_document: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', documentId);
      
      if (error) throw error;
      
      toast.success("Primary verification document updated");
      
    } catch (error: any) {
      console.error('Error updating primary document:', error);
      toast.error(error.message || 'Failed to update primary document');
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    verifyDocument,
    updatePrimaryDocumentStatus,
    isProcessing
  };
};
